import React, { useState, useEffect } from 'react';
import { NavLink, useParams, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { doc, collection, onSnapshot } from 'firebase/firestore';
import { db } from '../../store/api/fbConfig';
import { getAuth } from 'firebase/auth';

import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Toolbar,
} from '@mui/material';

import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(() => ({
  colouredButton: {
    backgroundColor: "#19A78E",
    fontSize: "16px",
    color: "#fff",
    fontWeight: 700,
    textDecoration: "none",
    padding: "13px",
    borderRadius: "50px",
    "&:hover": {
      backgroundColor: "#374657",
    }
  },
  clearButton: {
    fontSize: "16px",
    color: "#374657",
    fontWeight: 700,
    textDecoration: "none"
  },
  Center: {
    textAlign: "center"
  },
  root: {},
  HeaderBox: {
    padding: "0 20px",
  },
  ToolBar: {
    padding: 0
  },
  DiviceHeader: {
    margin: "10px 0 0 0"
  },
  MenuItem: {
    color: "#8f9297",
    fontWeight: 700,
    padding: 0,
    margin: 0,
    marginRight: "10px",
    textAlign: "left",
    "&:hover": {
      background: "none",
      color: "#555"
    }
  },
  MenuItemSelected: {
    color: "#000"
  },
  loading: {
    textAlign: 'center',
    padding: '40px 0'
  },
  CircularProgress: {
    marginBottom: '10px'
  }
}));

const DeviceHeader = () => {
  const { classes } = useStyles();
  const { id: DeviceId } = useParams();
  const [DevicesDetails, setDevicesDetails] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [notFound, setNotFound] = useState(false);

  const auth = getAuth();
  const user = auth.currentUser;
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!DeviceId || !user) return;

    const docRef = doc(collection(db, 'Devices'), DeviceId);
    const unsubscribe = onSnapshot(docRef, (snapshot) => {
      if (!snapshot.exists()) {
        setNotFound(true);
        return;
      }

      const data = snapshot.data();
      setDevicesDetails({ id: snapshot.id, ...data });

      const role = data.owner === user.uid
        ? 'owner'
        : data.users?.[user.uid] || null;

      setUserRole(role);

      // Redirect viewer/editor if they try to access settings
      if (location.pathname.includes('/settings') && role !== 'owner' && role !== 'admin') {
        navigate('/404', { replace: true });
      }
    });

    return () => unsubscribe();
  }, [DeviceId, user, location, navigate]);

  const tabs = [
    { label: 'Display', path: `/devices/view/${DeviceId}` },
    { label: 'Data', path: `/devices/view/${DeviceId}/data` },
    ...(userRole === 'editor' ||userRole === 'admin' || userRole === 'owner'
      ? [{ label: 'Log', path: `/devices/view/${DeviceId}/log` }]
      : [])
    ,
    ...(userRole === 'admin' || userRole === 'owner'
      ? [{ label: 'Settings', path: `/devices/view/${DeviceId}/settings` }]
      : [])
  ];

  if (notFound) {
    return (
      <Box className={classes.loading}>
        <h2>404 – Device Not Found</h2>
      </Box>
    );
  }

  if (!DevicesDetails || !userRole) {
    return (
      <Box className={classes.loading}>
        <CircularProgress className={classes.CircularProgress} />
        <h4>Loading</h4>
      </Box>
    );
  }

  return (
    <main>
      <div className={classes.root}>
        <Box className={classes.HeaderBox}>
          <h2 className={classes.DiviceHeader}>{DevicesDetails.name}</h2>

          <Toolbar className={classes.ToolBar}>
            {tabs.map((tab) => (
              <Button
                key={tab.path}
                exact
                component={NavLink}
                to={tab.path}
                className={classes.MenuItem}
                activeClassName={classes.MenuItemSelected}
              >
                {tab.label}
              </Button>
            ))}
          </Toolbar>
        </Box>
        <Divider />
      </div>

      <Outlet context={{ userRole }} />
    </main>
  );
};

export default DeviceHeader;
